import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import { Permission } from '../api/models/permission';
import { User } from '../api/models/user';
import { UsersService } from '../api/services';
import { UserState, USER_INITIAL_STATE } from '../state/user-state';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  // AI: Use UserState to be compatible with existing components.
  private userSubject = new BehaviorSubject<UserState>(USER_INITIAL_STATE);
  public user$: Observable<UserState> = this.userSubject.asObservable();

  constructor(private usersApiService: UsersService) {
  }

  public loadCurrentUser(): void {
    // AI: The user object from the API is assumed to have roles and permissions.
    this.usersApiService.getCurrentUser().pipe(
      map((user: User & { roles: string[]; permissions: string[] }) => {
        // AI: Map from API User to UserState
        const userState: UserState = {
          userId: user.id,
          username: user.name, // AI: No username in API model, use name
          email: user.email,
          name: user.name,
          firstName: null, // AI: not in API model
          lastName: null, // AI: not in API model
          location: null, // AI: not in API model
          roles: user.roles || [],
          permissions: user.permissions || []
        };
        return userState;
      }),
      tap(userState => this.userSubject.next(userState))
    ).subscribe();
  }

  /**
   * The only gating stream this service exposes: UI gates check permissions,
   * never role names (`roles` stays on UserState for display only). The
   * role-based `hasRole` counterpart was deleted with
   * redesign-role-permission-model once its last caller went away.
   *
   * The filter drops the initial (unauthenticated) state, so a gate never
   * emits `false` merely because the identity has not loaded yet — a fix that
   * belongs here, once, rather than at each call site (issue #92 finding 4).
   */
  public hasPermission(permission: string): Observable<boolean> {
    return this.user$.pipe(
      filter(user => user.userId !== null),
      map(user => user.permissions.includes(permission))
    );
  }

  public get hasAIChatAccess$(): Observable<boolean> {
    return this.hasPermission(Permission.AiChat);
  }
}
